<script setup lang="ts">
import WrVaahAgentsShowcaseCard from "./WrVaahAgentsShowcaseCard.vue";

interface ShowcaseItem {
    title: string;
    subtitle?: string;
    visual: "codegen" | "checklist" | "scaffold" | "review" | "integrate";
}

withDefaults(
    defineProps<{
        heading: string;
        headingAccent?: string;
        items: ShowcaseItem[];
    }>(),
    {
        headingAccent: "",
    }
);

/* ─── Warp Canvas Setup ─── */
const warpCanvas = ref<HTMLCanvasElement | null>(null);
let animationFrameId: number | null = null;
let resizeObserver: ResizeObserver | null = null;
let intersectionObserver: IntersectionObserver | null = null;
let visibilityHandler: (() => void) | null = null;
let stopAnimation: (() => void) | null = null;

onBeforeUnmount(() => {
    stopAnimation?.();
    resizeObserver?.disconnect();
    intersectionObserver?.disconnect();
    if (visibilityHandler) document.removeEventListener("visibilitychange", visibilityHandler);
});

onMounted(() => {
    const canvas = warpCanvas.value;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let width = 0;
    let height = 0;
    let dpr = 1;

    const count = 45;
    const speed = 8;
    const stars = new Float32Array(count * 4);

    const initStar = (i: number, resetZ = false) => {
        const idx = i * 4;
        stars[idx] = (Math.random() - 0.5) * (width * 1.8 || 1200);
        stars[idx + 1] = (Math.random() - 0.5) * (height * 1.2 || 400);
        stars[idx + 2] = resetZ ? width : Math.random() * width;
        stars[idx + 3] = Math.random() * 2.5 + 1.2;
    };

    const handleResize = () => {
        const parent = canvas.parentElement;
        if (!parent) return;

        dpr = Math.min(window.devicePixelRatio || 1, 2);
        width = parent.clientWidth;
        height = parent.clientHeight;

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);

        for (let i = 0; i < count; i++) {
            initStar(i);
        }
    };

    resizeObserver = new ResizeObserver(() => handleResize());
    if (canvas.parentElement) {
        resizeObserver.observe(canvas.parentElement);
    }
    handleResize();

    let inView = true;
    let running = false;

    const render = () => {
        if (!running) return;
        ctx.clearRect(0, 0, width, height);

        const cx = width / 2;
        const cy = height / 2;
        ctx.lineCap = "round";
        /* Light theme: the lavender streak heads would vanish on the light
           page background, so light mode draws deeper purple instead */
        const isDark = document.documentElement.classList.contains("my-app-dark");

        for (let i = 0; i < count; i++) {
            const idx = i * 4;
            const x = stars[idx]!;
            const y = stars[idx + 1]!;
            const currentZ = stars[idx + 2]!;
            const thickness = stars[idx + 3]!;

            const tailZ = currentZ + 180;
            const prevX = (x / tailZ) * width + cx;
            const prevY = (y / tailZ) * height + cy;

            stars[idx + 2] = currentZ - speed;

            if (currentZ - speed <= 10) {
                initStar(i, true);
                continue;
            }

            const currX = (x / currentZ) * width + cx;
            const currY = (y / currentZ) * height + cy;

            const progress = 1 - currentZ / width;
            const alpha = Math.min(progress * 1.4, 0.65);

            const gradient = ctx.createLinearGradient(prevX, prevY, currX, currY);
            gradient.addColorStop(0, isDark ? "rgba(139, 92, 246, 0)" : "rgba(124, 58, 237, 0)");
            gradient.addColorStop(0.6, `rgba(124, 58, 237, ${alpha * (isDark ? 0.4 : 0.5)})`);
            gradient.addColorStop(1, isDark ? `rgba(196, 181, 253, ${alpha})` : `rgba(109, 45, 158, ${alpha})`);

            ctx.beginPath();
            ctx.moveTo(prevX, prevY);
            ctx.lineTo(currX, currY);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = thickness * (1 + progress * 2);
            ctx.stroke();
        }

        animationFrameId = requestAnimationFrame(render);
    };

    const start = () => {
        if (running) return;
        running = true;
        animationFrameId = requestAnimationFrame(render);
    };

    const stop = () => {
        if (!running) return;
        running = false;
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
    stopAnimation = stop;

    const update = () => {
        if (!document.hidden && inView) start();
        else stop();
    };

    visibilityHandler = () => update();
    document.addEventListener("visibilitychange", visibilityHandler);

    intersectionObserver = new IntersectionObserver((entries) => {
        inView = entries[0]?.isIntersecting ?? true;
        update();
    });
    intersectionObserver.observe(canvas);

    update();
});
</script>

<template>
    <section class="va-show">
        <!-- ── Heading + Warp Canvas ── -->
        <div class="relative overflow-hidden py-[60px] flex justify-center items-center">
            <canvas ref="warpCanvas"
                class="va-show-warp-canvas absolute inset-0 w-full h-full pointer-events-none z-0 blur-[1px]"
                aria-hidden="true"></canvas>
            <h2 class="relative z-10 text-[clamp(30px,4.6vw,61px)] leading-[1.2] tracking-0 text-center m-0">
                <span
                    class="block font-avant-demi font-semibold bg-gradient-to-r from-[#12dbff] to-[#4d7cfe] bg-clip-text text-transparent">{{
                    heading }}</span>
                <span v-if="headingAccent" class="block font-avant font-normal text-[#1a1a1a] dark:text-white">{{
                    headingAccent }}</span>
            </h2>
        </div>

        <!-- ── Card grid ── -->
        <div class="grid grid-cols-1 gap-8 mt-11 px-4 max-w-[1180px] mx-auto md:grid-cols-2 md:gap-9 lg:grid-cols-6">
            <WrVaahAgentsShowcaseCard v-for="(item, i) in items" :key="item.title" :item="item" :large="i < 2" />
        </div>
    </section>
</template>

<style scoped>
/* Warp canvas: radial mask (the 1px blur is the blur-[1px] utility) */
.va-show-warp-canvas {
    mask-image: radial-gradient(ellipse 90% 60% at 50% 50%, #000 40%, transparent 95%);
    -webkit-mask-image: radial-gradient(ellipse 90% 60% at 50% 50%, #000 40%, transparent 95%);
}
</style>
